// Shared DRRDSS report template (jsPDF).
//
// Matches the branded advisory design supplied as reference (the
// "TIMOR LESTE <HAZARD> ADVISORY" SVGs — orange header with the APC and
// DNMG emblems, a light-green event-summary card, colour-coded content
// cards, and a dark-blue footer bar). Every module's "Generate Report" /
// "Generate Advisory" button should build its PDF on these helpers instead
// of hand-rolling its own header/footer, so every report issued by the
// platform shares one visual identity.
//
// Usage sketch (see RecentEarthquake.js / ReportGenerator_FR.js for full
// examples):
//
//   const pdf = new jsPDF();
//   await drawReportHeader(pdf, {
//     title: "TIMOR LESTE EARTHQUAKE ADVISORY",
//     issuedBy: "DNMG-APC", issuedAt: "19 October 2026 * 10:35 TLT",
//   });
//   let y = CONTENT_TOP;
//   y = drawEventSummaryCard(pdf, y, { heading: "EARTHQUAKE EVENT", ... });
//   y = drawColorCardRow(pdf, y, [ {bg: COLORS.cardGreen, heading: "INDOORS", bullets:[...]}, ... ]);
//   drawReportFooter(pdf, { message: "Aftershocks may occur..." });
//   pdf.save("Earthquake_Advisory.pdf");

// ---------------------------------------------------------------------------
// Palette — sampled directly from the reference SVG artwork, not guessed.
// Keep in sync with backend_repo/common/report_template.py.
// ---------------------------------------------------------------------------
export const COLORS = {
  headerOrange: "#FF914D",
  titleNavy: "#1A1A8F",
  bodyInk: "#1F1F1F",
  muted: "#555555",
  footerBlue: "#004AAD",
  cardGreen: "#E8F8D5",
  cardTan: "#F6E2B5",
  cardOrange: "#EDC289", // the Fire Risk "High" badge's own card background — distinct from cardTan
  cardLavender: "#C2CDFF",
  cardPink: "#FDDEDE",
  red: "#FF5757",
  white: "#FFFFFF",
  // Risk-level scale shared by every hazard that classes severity this way
  riskExtreme: "#FF2A2A",
  riskHigh: "#FF6701",
  riskModerate: "#FFED08",
  riskLow: "#00BF63",
  riskNone: "#DEDEDF",
};

export function riskColor(level) {
  const key = String(level || "").trim().toLowerCase();
  return (
    {
      extreme: COLORS.riskExtreme,
      high: COLORS.riskHigh,
      moderate: COLORS.riskModerate,
      low: COLORS.riskLow,
      none: COLORS.riskNone,
      "no threat": COLORS.riskNone,
    }[key] || COLORS.riskNone
  );
}

const PAGE_W = 210; // A4 mm
const PAGE_H = 297;
const MARGIN = 10;
export const HEADER_H = 32;
export const FOOTER_H = 16;
export const CONTENT_TOP = HEADER_H + 6;
export const CONTENT_BOTTOM = PAGE_H - FOOTER_H - 6;
export const CONTENT_WIDTH = PAGE_W - 2 * MARGIN;

const APC_LOGO_SRC = "/report-assets/apc-logo.png";
const DNMG_LOGO_SRC = "/report-assets/dnmg-logo.png";

const hexToRgb = (hex) => {
  const h = hex.replace("#", "");
  return [
    parseInt(h.substring(0, 2), 16),
    parseInt(h.substring(2, 4), 16),
    parseInt(h.substring(4, 6), 16),
  ];
};

const setFill = (pdf, hex) => pdf.setFillColor(...hexToRgb(hex));
const setText = (pdf, hex) => pdf.setTextColor(...hexToRgb(hex));
const setDraw = (pdf, hex) => pdf.setDrawColor(...hexToRgb(hex));

// Logos are loaded once and cached as data URLs so every report that calls
// drawReportHeader in the same page session doesn't re-fetch them.
let logoCache = null;
const loadImageAsDataURL = (src) =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        canvas.getContext("2d").drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      } catch (e) {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });

async function getLogos() {
  if (!logoCache) {
    logoCache = Promise.all([
      loadImageAsDataURL(APC_LOGO_SRC),
      loadImageAsDataURL(DNMG_LOGO_SRC),
    ]);
  }
  return logoCache;
}

/**
 * Pre-loads (and caches) the two header logos as data URLs. Call this once,
 * `await`ed, before any synchronous context that needs to draw the header —
 * e.g. jspdf-autotable's `didDrawPage`, which isn't async-aware — then use
 * drawReportHeaderSync from inside that callback.
 */
export async function preloadReportLogos() {
  return getLogos();
}

function paintHeader(pdf, [apcLogo, dnmgLogo], { title, subtitle = "DISASTER RISK REDUCTION DECISION SUPPORT SYSTEM", issuedBy = "DNMG-APC", issuedAt = "" }) {
  setFill(pdf, COLORS.headerOrange);
  pdf.rect(0, 0, PAGE_W, HEADER_H, "F");

  const logoD = 20;
  const logoY = (HEADER_H - logoD) / 2;
  if (apcLogo) {
    try {
      pdf.addImage(apcLogo, "PNG", MARGIN, logoY, logoD, logoD);
    } catch (e) { /* missing/broken asset — header text still renders */ }
  }
  if (dnmgLogo) {
    try {
      pdf.addImage(dnmgLogo, "PNG", PAGE_W - MARGIN - logoD, logoY, logoD, logoD);
    } catch (e) { /* missing/broken asset — header text still renders */ }
  }

  setText(pdf, COLORS.titleNavy);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(15);
  pdf.text(title.toUpperCase(), PAGE_W / 2, 13, { align: "center" });
  pdf.setFontSize(9.5);
  pdf.text(subtitle, PAGE_W / 2, 19, { align: "center" });
  pdf.setFontSize(8);
  pdf.text(`ISSUED BY: ${issuedBy} * ${issuedAt}`, PAGE_W / 2, 24.5, { align: "center" });
}

/**
 * Draws the branded header band (orange background, APC + DNMG logos,
 * title/subtitle/issued-by text). Call once per page if a report spans
 * more than one page — see drawReportFooter for the matching footer. Use
 * drawReportHeaderSync instead from inside a non-async callback.
 */
export async function drawReportHeader(pdf, opts) {
  const logos = await getLogos();
  paintHeader(pdf, logos, opts);
}

/**
 * Synchronous header draw for use inside jspdf-autotable's `didDrawPage` and
 * similar non-async callbacks. The logos MUST already be warmed — call
 * `await preloadReportLogos()` once before the autoTable(...) call.
 */
export function drawReportHeaderSync(pdf, opts, logos) {
  paintHeader(pdf, logos, opts);
}

/**
 * Draws the branded footer bar (dark-blue, alert headline + message +
 * contact line). Call once per page.
 */
export function drawReportFooter(
  pdf,
  {
    headline = "IMPORTANT",
    message = "",
    contact = "In emergency please call: 115 (APC); 112 (PNTL); 110 (SNAEM)  drr.dnmg.gov.tl * geofisica@dnmg.gov.tl * +670 7709 4893",
  } = {}
) {
  const y = PAGE_H - FOOTER_H - 4;
  setFill(pdf, COLORS.footerBlue);
  pdf.roundedRect(MARGIN, y, CONTENT_WIDTH, FOOTER_H, 2, 2, "F");

  setFill(pdf, COLORS.white);
  pdf.circle(MARGIN + 6, y + FOOTER_H / 2, 2.6, "F");
  setText(pdf, COLORS.footerBlue);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(8);
  pdf.text("!", MARGIN + 6, y + FOOTER_H / 2 + 1.1, { align: "center" });

  setText(pdf, COLORS.white);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(8.5);
  pdf.text(headline, MARGIN + 12, y + 5);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(7);
  pdf.text(String(message).slice(0, 150), MARGIN + 12, y + 9.5);
  pdf.setFontSize(5.6);
  pdf.text(String(contact).slice(0, 170), MARGIN + 12, y + 13.5);
}

/**
 * The light-green event-summary card: a big coloured value badge, a
 * headline + detail lines, and an optional boxed side readout (e.g.
 * "TSUNAMI: NONE"). Returns the y position immediately below the card.
 */
export function drawEventSummaryCard(
  pdf,
  y,
  { heading, detailLines = [], badgeText, badgeColor = COLORS.red, sideLabel, sideValue, sideValueColor = COLORS.riskLow, height = 24 }
) {
  setFill(pdf, COLORS.cardGreen);
  pdf.roundedRect(MARGIN, y, CONTENT_WIDTH, height, 2.5, 2.5, "F");

  let textX = MARGIN + 5;
  if (badgeText) {
    const r = height / 2 - 3;
    const cx = MARGIN + 5 + r;
    const cy = y + height / 2;
    setFill(pdf, badgeColor);
    pdf.circle(cx, cy, r, "F");
    setText(pdf, COLORS.white);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(12);
    pdf.text(String(badgeText), cx, cy + 1.6, { align: "center" });
    textX = MARGIN + 5 + 2 * r + 6;
  }

  setText(pdf, COLORS.bodyInk);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(10);
  pdf.text(heading, textX, y + 7);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(7.5);
  let ty = y + 11.5;
  detailLines.forEach((line) => {
    pdf.text(String(line), textX, ty);
    ty += 4.2;
  });

  if (sideLabel) {
    const boxW = 32;
    const boxH = height - 6;
    const boxX = MARGIN + CONTENT_WIDTH - boxW - 3;
    const boxY = y + 3;
    setDraw(pdf, "#CCCCCC");
    setFill(pdf, COLORS.white);
    pdf.roundedRect(boxX, boxY, boxW, boxH, 1.5, 1.5, "FD");
    setText(pdf, COLORS.muted);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(7);
    pdf.text(sideLabel, boxX + boxW / 2, boxY + 5, { align: "center" });
    setText(pdf, sideValueColor);
    pdf.setFontSize(8.5);
    pdf.text(String(sideValue), boxX + boxW / 2, boxY + boxH / 2 + 3, { align: "center" });
  }

  return y + height;
}

/**
 * One row of evenly-spaced coloured cards (2, 3 or 4 across). Each card
 * carries either bullet lines or a single big number (for stat/count
 * cards like Fire Risk's Extreme/High/Moderate/Low tiles). Returns the y
 * position immediately below the row.
 */
export function drawColorCardRow(pdf, y, cards, { height = 26, gap = 3 } = {}) {
  const n = cards.length;
  const colW = (CONTENT_WIDTH - gap * (n - 1)) / n;

  cards.forEach((card, i) => {
    const x = MARGIN + i * (colW + gap);
    setFill(pdf, card.bg);
    pdf.roundedRect(x, y, colW, height, 2, 2, "F");

    if (card.bigNumber !== undefined) {
      const r = Math.min(9, height / 2 - 4);
      const cx = x + colW / 2;
      const cy = y + height / 2 + 1;
      setText(pdf, COLORS.bodyInk);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(8);
      pdf.text(card.heading, x + colW / 2, y + 6, { align: "center" });
      setFill(pdf, card.numberColor || COLORS.bodyInk);
      pdf.circle(cx, cy, r, "F");
      setText(pdf, COLORS.white);
      pdf.setFontSize(10);
      pdf.text(String(card.bigNumber), cx, cy + 1.3, { align: "center" });
      return;
    }

    setText(pdf, COLORS.bodyInk);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(8);
    pdf.text(card.heading, x + 3, y + 6);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(6.8);
    let ty = y + 10.5;
    (card.bullets || []).forEach((b) => {
      const text = `• ${b}`;
      const lines = pdf.splitTextToSize(text, colW - 6);
      lines.forEach((ln) => {
        if (ty < y + height - 1.5) {
          pdf.text(ln, x + 3, ty);
          ty += 3.4;
        }
      });
    });
  });

  return y + height;
}

/**
 * A bordered data table in the report's own look. `rows` is an array of
 * string arrays; pass `advisoryColIndex` to colour that column's cells by
 * risk level (e.g. the "Advisory" column on a highest-risk table). Uses
 * jsPDF-AutoTable if available on the pdf instance (pdf.autoTable), which
 * every report generator in this app already loads.
 */
export function drawStyledTable(pdf, y, { head, rows, advisoryColIndex }) {
  const bodyStyles = { fontSize: 7.5, textColor: hexToRgb(COLORS.bodyInk) };
  const headStyles = { fillColor: hexToRgb(COLORS.footerBlue), textColor: hexToRgb(COLORS.white), fontStyle: "bold" };
  const didParseCell =
    advisoryColIndex === undefined
      ? undefined
      : (data) => {
          if (data.section === "body" && data.column.index === advisoryColIndex) {
            const level = String(data.cell.raw).replace(/ Risk$/i, "");
            data.cell.styles.fillColor = hexToRgb(riskColor(level));
          }
        };
  pdf.autoTable({
    startY: y,
    head: [head],
    body: rows,
    margin: { left: MARGIN, right: MARGIN },
    styles: bodyStyles,
    headStyles,
    didParseCell,
  });
  return pdf.lastAutoTable.finalY + 4;
}

/** Starts a new page and redraws the header for it (for multi-page reports). */
export async function newReportPage(pdf, headerOpts) {
  pdf.addPage();
  await drawReportHeader(pdf, headerOpts);
  return CONTENT_TOP;
}
